import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';

const AuthContext = createContext({});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tokenValidated, setTokenValidated] = useState(false);
  const [authError, setAuthError] = useState(null);

  useEffect(() => {
    const loadStoredAuth = async () => {
      setLoading(true);
      console.log('AuthProvider: Carregando dados de autenticação armazenados');
      
      const storedUser = localStorage.getItem('user');
      const token = localStorage.getItem('token');
      
      console.log('Token armazenado:', token ? 'Sim (token existe)' : 'Não');
      console.log('Usuário armazenado:', storedUser ? 'Sim (dados existem)' : 'Não');
      
      if (storedUser && token) {
        try {
          const userData = JSON.parse(storedUser);
          console.log('Dados do usuário carregados:', { id: userData.id, nome: userData.nome, email: userData.email, perfil: userData.perfil });
          setUser(userData);
          
          // Configurar token para todas as requisições
          api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          console.log('Token configurado nos headers padrão da API');
          
          console.log('Validando token com o servidor...');
          // Verificar se o token é válido
          try {
            // Fazer uma requisição simples para verificar se o token é válido
            console.log('Enviando requisição para /api/users/me');
            const response = await api.get('/api/users/me');
            console.log('Token validado com sucesso:', response.data);
            setTokenValidated(true);
            setAuthError(null);
          } catch (error) {
            console.error('Erro ao validar token:', error);
            console.error('Detalhes do erro de validação:', {
              message: error.message,
              status: error.response?.status,
              data: error.response?.data
            });
            
            // Se o token for inválido, fazer logout
            if (error.response && error.response.status === 401) {
              console.log('Token inválido, fazendo logout');
              setAuthError('Token inválido ou expirado');
              signOut();
            } else {
              // Se for outro tipo de erro (como erro de conexão), não fazer logout
              console.log('Erro de conexão ao validar token, mantendo dados locais');
              setAuthError('Erro de conexão ao validar credenciais');
              // Manter o usuário logado, mas marcar o token como não validado
              setTokenValidated(false);
            }
          }
        } catch (error) {
          console.error('Erro ao processar dados de autenticação armazenados:', error);
          setAuthError('Erro ao processar dados de autenticação');
          signOut();
        }
      } else {
        console.log('Nenhum dado de autenticação encontrado');
        setUser(null);
        setTokenValidated(false);
        setAuthError(null);
      }
      
      setLoading(false);
    };
    
    loadStoredAuth();
  }, []);

  const signIn = async (email, password) => {
    try {
      console.log('Tentando login com email:', email);
      setLoading(true);
      setAuthError(null);
      
      // Garantir que não haja token antigo nos headers
      delete api.defaults.headers.common['Authorization'];
      
      console.log('Enviando requisição de login para:', `${api.defaults.baseURL}/api/auth/login`);
      const response = await api.post('/api/auth/login', { email, password });
      const { token, user: userData } = response.data;

      console.log('Login bem-sucedido:', { id: userData.id, nome: userData.nome, email: userData.email, perfil: userData.perfil });
      console.log('Token recebido:', token ? 'Sim (token recebido)' : 'Não');
      
      // Primeiro configuramos o token nos headers da API
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      console.log('Token configurado nos headers da API após login');
      
      // Depois atualizamos o estado do contexto
      setUser(userData);
      setTokenValidated(true);
      
      // Por último, salvamos no localStorage
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(userData));
      
      setLoading(false);
      return userData;
    } catch (error) {
      console.error('Erro no login:', error);
      console.error('Detalhes do erro de login:', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data
      });
      
      setLoading(false);
      
      // Definir mensagem de erro apropriada
      if (error.response) {
        if (error.response.status === 401) {
          setAuthError('Credenciais inválidas');
        } else if (error.response.status === 403) {
          setAuthError('Acesso negado');
        } else {
          setAuthError(`Erro no servidor: ${error.response.data?.message || 'Erro desconhecido'}`);
        }
      } else if (error.request) {
        setAuthError('Erro de conexão com o servidor');
      } else {
        setAuthError(`Erro: ${error.message}`);
      }
      
      throw error;
    }
  };

  const signOut = () => {
    console.log('Realizando logout');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete api.defaults.headers.common['Authorization'];
    setUser(null);
    setTokenValidated(false);
    setAuthError(null);
  };

  // Função para verificar token manualmente quando necessário
  const validateToken = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      console.log('Sem token para validar');
      setTokenValidated(false);
      return false;
    }

    try {
      console.log('Validando token manualmente...');
      console.log('URL da requisição:', `${api.defaults.baseURL}/api/users/me`);
      
      // Garantir que o token esteja configurado no header
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      console.log('Token configurado nos headers para validação manual');
      
      const response = await api.get('/api/users/me');
      console.log('Token validado com sucesso:', response.data);
      
      // Atualizar o usuário se necessário
      if (response.data && response.data.user) {
        setUser(response.data.user);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        console.log('Dados do usuário atualizados após validação');
      }
      
      setTokenValidated(true);
      setAuthError(null);
      return true;
    } catch (error) {
      console.error('Erro ao validar token manualmente:', error);
      console.error('Detalhes do erro de validação manual:', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
        config: {
          url: error.config?.url,
          baseURL: error.config?.baseURL,
          headers: error.config?.headers
        }
      });
      
      if (error.response && error.response.status === 401) {
        console.log('Token inválido na validação manual, fazendo logout');
        setAuthError('Token inválido ou expirado');
        signOut();
      } else {
        console.log('Erro de conexão na validação manual, mantendo estado atual');
        setAuthError('Erro de conexão ao validar credenciais');
      }
      
      setTokenValidated(false);
      return false;
    }
  };

  return (
    <AuthContext.Provider value={{ 
      signed: !!user && tokenValidated, 
      user, 
      setUser, 
      signIn, 
      signOut, 
      loading,
      tokenValidated,
      validateToken,
      authError
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
